import { useEffect, useState } from "react";
import { CloseIcon } from "../../../components/Icons";
import { MailParameterReference } from "../../../components/term/MailParameterReference";
import { MailTemplateEditor } from "../../../components/term/MailTemplateEditor";
import {
  SPONSOR_REMINDER_MAIL_DEFAULT,
  SPONSOR_REMINDER_MAIL_PARAMETERS,
} from "../../../components/term/scheduleMailTemplate";
import type { SponsorReminderSetting } from "../../../hooks";
import { useUpsertSponsorReminderSetting } from "../../../hooks";

interface SponsorReminderMailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  termId: string;
  // 未設定なら null（term に1:1。保存時に upsert される）
  setting: SponsorReminderSetting | null;
  onSaved: () => void;
}

// 送信日は「回答期限のN日前（relative）」か「絶対日（absolute）」のどちらかで指定する
type ConditionMode = "relative" | "absolute";

interface ConditionFormState {
  mode: ConditionMode;
  daysBeforeDeadline: string;
  sendDate: string;
  completionRateThreshold: string;
}

function buildFormState(setting: SponsorReminderSetting | null) {
  return {
    title: setting?.title ?? SPONSOR_REMINDER_MAIL_DEFAULT.title,
    description:
      setting?.description ?? SPONSOR_REMINDER_MAIL_DEFAULT.description,
    // 宛先が未登録でも入力欄を1つ表示するため空文字を1件置く
    recipientEmails:
      setting && setting.recipientEmails.length > 0
        ? setting.recipientEmails
        : [""],
    enabled: setting?.enabled ?? false,
    conditions: (setting?.conditions ?? []).map((condition) => ({
      mode: (condition.sendDate ? "absolute" : "relative") as ConditionMode,
      daysBeforeDeadline: condition.daysBeforeDeadline?.toString() ?? "",
      sendDate: condition.sendDate ?? "",
      completionRateThreshold:
        condition.completionRateThreshold?.toString() ?? "",
    })),
  };
}

function emptyCondition(): ConditionFormState {
  return {
    mode: "relative",
    daysBeforeDeadline: "",
    sendDate: "",
    completionRateThreshold: "",
  };
}

function toOptionalInt(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed === "") return null;
  const parsed = Number.parseInt(trimmed, 10);
  return Number.isNaN(parsed) ? null : parsed;
}

export function SponsorReminderMailDialog({
  open,
  onOpenChange,
  termId,
  setting,
  onSaved,
}: SponsorReminderMailDialogProps) {
  const { upsertSponsorReminderSetting } = useUpsertSponsorReminderSetting();
  const [form, setForm] = useState(() => buildFormState(setting));

  // 開くたびに最新の setting から再シードする
  useEffect(() => {
    if (open) {
      setForm(buildFormState(setting));
    }
  }, [open, setting]);

  if (!open) return null;

  const updateRecipientAt = (index: number, value: string) => {
    setForm((prev) => ({
      ...prev,
      recipientEmails: prev.recipientEmails.map((email, i) =>
        i === index ? value : email,
      ),
    }));
  };

  const addRecipient = () => {
    setForm((prev) => ({
      ...prev,
      recipientEmails: [...prev.recipientEmails, ""],
    }));
  };

  const removeRecipientAt = (index: number) => {
    setForm((prev) => {
      const next = prev.recipientEmails.filter((_, i) => i !== index);
      return { ...prev, recipientEmails: next.length > 0 ? next : [""] };
    });
  };

  const updateConditionAt = (
    index: number,
    patch: Partial<ConditionFormState>,
  ) => {
    setForm((prev) => ({
      ...prev,
      conditions: prev.conditions.map((condition, i) =>
        i === index ? { ...condition, ...patch } : condition,
      ),
    }));
  };

  const addCondition = () => {
    setForm((prev) => ({
      ...prev,
      conditions: [...prev.conditions, emptyCondition()],
    }));
  };

  const removeConditionAt = (index: number) => {
    setForm((prev) => ({
      ...prev,
      conditions: prev.conditions.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title.trim()) {
      alert("件名を入力してください");
      return;
    }
    // 空欄・重複・前後空白を除去して送信（保存はメールアドレス集合）
    const recipientEmails = [
      ...new Set(
        form.recipientEmails.map((email) => email.trim()).filter(Boolean),
      ),
    ];
    // モードに応じて送信日は片方だけ値を持たせる。何も入っていない条件は除外する
    const conditions = form.conditions
      .map((condition) => ({
        daysBeforeDeadline:
          condition.mode === "relative"
            ? toOptionalInt(condition.daysBeforeDeadline)
            : null,
        sendDate:
          condition.mode === "absolute" ? condition.sendDate || null : null,
        completionRateThreshold: toOptionalInt(
          condition.completionRateThreshold,
        ),
      }))
      .filter(
        (condition) =>
          condition.daysBeforeDeadline !== null ||
          condition.sendDate !== null ||
          condition.completionRateThreshold !== null,
      );
    try {
      const result = await upsertSponsorReminderSetting({
        termId,
        title: form.title,
        description: form.description,
        enabled: form.enabled,
        recipientEmails,
        conditions,
      });
      if (result) {
        onOpenChange(false);
        onSaved();
        alert("スポンサー向けリマインドメールを更新しました");
      } else {
        alert("更新に失敗しました");
      }
    } catch (err) {
      alert(err instanceof Error ? err.message : "保存に失敗しました");
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={() => onOpenChange(false)}
    >
      <div
        className="max-h-[90vh] w-[90%] max-w-[600px] overflow-auto rounded-xl bg-white p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-6 flex items-center justify-between">
          <h3 className="font-bold text-lg">
            スポンサー向けリマインドメールを編集
          </h3>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="cursor-pointer border-none bg-none p-1"
          >
            <CloseIcon className="h-5 w-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <p className="text-[var(--color-text-secondary)] text-xs">
            対象者の対応が期限までに完了しない場合に、スポンサー担当者（人事）へ声がけを依頼するメールです
          </p>

          {/* 送信タイミング（自動送信の条件・複数登録可） */}
          <div className="rounded-md border border-[var(--color-border-default)] bg-[var(--color-bg-subtle-gray)] p-3">
            <label className="flex items-center font-medium text-sm">
              <input
                type="checkbox"
                checked={form.enabled}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, enabled: e.target.checked }))
                }
                className="mr-2"
              />
              自動送信を有効にする
            </label>
            <p className="mt-1 mb-2 text-[var(--color-text-secondary)] text-xs">
              送信タイミングを複数登録できます（各条件を満たしたときに送信）
            </p>
            <div className="flex flex-col gap-2">
              {form.conditions.map((condition, index) => (
                <div
                  // 条件行は並び順で識別する（値は重複・編集途中がありうるためキーにできない）
                  // biome-ignore lint/suspicious/noArrayIndexKey: 条件行は順序で管理する
                  key={index}
                  className="flex flex-wrap items-center gap-2 rounded-md border border-[var(--color-border-default)] bg-white px-2 py-2 text-sm"
                >
                  <select
                    value={condition.mode}
                    onChange={(e) =>
                      updateConditionAt(index, {
                        mode: e.target.value as ConditionMode,
                      })
                    }
                    className="rounded-md border border-[var(--color-border-default)] px-2 py-1.5 text-sm"
                  >
                    <option value="relative">回答期限の</option>
                    <option value="absolute">日付指定</option>
                  </select>
                  {condition.mode === "relative" ? (
                    <>
                      <input
                        type="number"
                        min="0"
                        value={condition.daysBeforeDeadline}
                        onChange={(e) =>
                          updateConditionAt(index, {
                            daysBeforeDeadline: e.target.value,
                          })
                        }
                        className="w-16 rounded-md border border-[var(--color-border-default)] px-2 py-1.5 text-sm"
                      />
                      <span>日前</span>
                    </>
                  ) : (
                    <input
                      type="date"
                      value={condition.sendDate}
                      onChange={(e) =>
                        updateConditionAt(index, { sendDate: e.target.value })
                      }
                      className="rounded-md border border-[var(--color-border-default)] px-2 py-1.5 text-sm"
                    />
                  )}
                  <span>に、完了率</span>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={condition.completionRateThreshold}
                    onChange={(e) =>
                      updateConditionAt(index, {
                        completionRateThreshold: e.target.value,
                      })
                    }
                    className="w-16 rounded-md border border-[var(--color-border-default)] px-2 py-1.5 text-sm"
                  />
                  <span>% 未満</span>
                  <button
                    type="button"
                    onClick={() => removeConditionAt(index)}
                    className="ml-auto cursor-pointer rounded-md border border-[var(--color-border-default)] bg-white px-2 py-1.5 text-[var(--color-text-secondary)] text-xs"
                  >
                    削除
                  </button>
                </div>
              ))}
              {form.conditions.length === 0 && (
                <p className="text-[var(--color-text-secondary)] text-xs">
                  条件が未登録です
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={addCondition}
              className="mt-2 cursor-pointer rounded-md border border-[var(--color-primary-500)] bg-white px-3 py-1.5 font-medium text-[var(--color-primary-500)] text-sm"
            >
              ＋ 条件を追加
            </button>
            <p className="mt-2 text-[var(--color-text-secondary)] text-xs">
              ※「回答期限の◯日前」の条件は土日を除いた平日に送信されます。「日付指定」の条件は指定した日（土日を含む）に送信されます。
            </p>
          </div>

          {/* 送信対象（宛先メールアドレス） */}
          <div>
            <label className="mb-1 block font-medium text-sm">
              送信対象（メールアドレス）
            </label>
            <div className="flex flex-col gap-2">
              {form.recipientEmails.map((email, index) => (
                <div
                  // 入力欄は並び順で識別する（メール値はキーにできない: 重複・空欄・編集途中がありうる）
                  // biome-ignore lint/suspicious/noArrayIndexKey: 宛先入力欄は順序で管理する固定リスト
                  key={index}
                  className="flex items-center gap-2"
                >
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => updateRecipientAt(index, e.target.value)}
                    placeholder="hr@example.com"
                    className="flex-1 rounded-md border border-[var(--color-border-default)] px-3 py-2 text-sm"
                  />
                  <button
                    type="button"
                    onClick={() => removeRecipientAt(index)}
                    className="cursor-pointer rounded-md border border-[var(--color-border-default)] bg-white px-3 py-2 text-[var(--color-text-secondary)] text-sm"
                  >
                    削除
                  </button>
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={addRecipient}
              className="mt-2 cursor-pointer rounded-md border border-[var(--color-primary-500)] bg-white px-3 py-1.5 font-medium text-[var(--color-primary-500)] text-sm"
            >
              ＋ 宛先を追加
            </button>
          </div>

          <hr className="border-[var(--color-border-default)]" />

          <div>
            <label className="mb-1 block font-medium text-sm">件名</label>
            <MailTemplateEditor
              singleLine
              parameters={SPONSOR_REMINDER_MAIL_PARAMETERS}
              value={form.title}
              onChange={(text) => setForm((prev) => ({ ...prev, title: text }))}
            />
          </div>
          <div>
            <label className="mb-1 block font-medium text-sm">本文</label>
            <MailTemplateEditor
              parameters={SPONSOR_REMINDER_MAIL_PARAMETERS}
              value={form.description}
              onChange={(text) =>
                setForm((prev) => ({ ...prev, description: text }))
              }
            />
            <p className="mt-1 text-[var(--color-primary-500)] text-xs">
              <span className="font-mono">@</span>{" "}
              を入力するとパラメータを挿入できます（予約状況・未予約者リストは送信時に自動生成されます）
            </p>
            <MailParameterReference
              parameters={SPONSOR_REMINDER_MAIL_PARAMETERS}
            />
          </div>

          <button
            type="submit"
            className="cursor-pointer rounded-md border-none bg-[var(--color-primary-500)] px-4 py-2.5 font-medium text-sm text-white"
          >
            更新
          </button>
        </form>
      </div>
    </div>
  );
}
